import parseCsv from './parseCsv';

const cellText = value => value == null ? '' : value instanceof Date ? value.toISOString() : typeof value === 'object' ? JSON.stringify(value) : String(value);

function flattenRecord(record, prefix = '', depth = 0) {
  if (!record || Array.isArray(record) || typeof record !== 'object') throw new Error('Each candidate must be an object with named fields.');
  if (depth > 10) throw new Error('Candidate data is nested too deeply.');
  return Object.fromEntries(Object.entries(record).flatMap(([name, value]) => {
    const path = prefix ? `${prefix}.${name}` : name;
    return value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length
      ? Object.entries(flattenRecord(value, path, depth + 1)) : [[path, cellText(value)]];
  }));
}

export function parseCandidateRecords(value, maxRows = 5000) {
  let records = value;
  if (!Array.isArray(records) && records && typeof records === 'object') {
    records = ['candidates', 'people', 'records', 'data', 'rows', 'applications'].map(key => records[key]).find(Array.isArray) || [records];
  }
  if (!Array.isArray(records) || !records.length) throw new Error('The file has no candidate records.');
  if (records.length > maxRows) throw new Error(`Import up to ${maxRows} candidates at a time.`);
  const flattened = records.map(record => flattenRecord(record));
  const headers = [...new Set(flattened.flatMap(Object.keys))];
  if (!headers.length || headers.length > 500 || headers.some(h => !h.trim() || h.length > 500)) throw new Error('Use 1–500 named columns, with headers up to 500 characters.');
  return { headers, rows: flattened.map(record => Object.fromEntries(headers.map(h => [h, record[h] ?? '']))) };
}

export function parseCandidateText(text, extension, maxRows = 5000) {
  const source = String(text).replace(/^\uFEFF/, '');
  if (extension === 'json') return parseCandidateRecords(JSON.parse(source), maxRows);
  if (['jsonl', 'ndjson'].includes(extension)) return parseCandidateRecords(source.split(/\r?\n/).filter(line => line.trim()).map((line, index) => {
    try { return JSON.parse(line); } catch { throw new Error(`Invalid JSON on line ${index + 1}.`); }
  }), maxRows);
  if (extension === 'xml') {
    if (/<!DOCTYPE|<!ENTITY/i.test(source)) throw new Error('XML document types and entities are not supported.');
    const doc = new DOMParser().parseFromString(source, 'application/xml');
    if (doc.querySelector('parsererror')) throw new Error('The XML file is not valid.');
    const nodeValue = node => {
      const children = [...node.children];
      if (!children.length && !node.attributes.length) return node.textContent;
      const entries = [...node.attributes].map(attr => [`@${attr.name}`, attr.value]);
      const names = [...new Set(children.map(child => child.nodeName))];
      for (const name of names) {
        const values = children.filter(child => child.nodeName === name).map(nodeValue);
        entries.push([name, values.length === 1 ? values[0] : values]);
      }
      if (!children.length) entries.push(['value', node.textContent]);
      return Object.fromEntries(entries);
    };
    const children = [...doc.documentElement.children];
    const records = children.length && children.every(child => child.nodeName === children[0].nodeName) ? children : [doc.documentElement];
    return parseCandidateRecords(records.map(nodeValue), maxRows);
  }
  // Detect the delimiter from the header only, respecting quoted header names.
  const firstLine = source.split(/\r?\n/, 1)[0];
  const counts = { ',': 0, ';': 0, '\t': 0 }; let quoted = false;
  for (let i = 0; i < firstLine.length; i++) {
    if (firstLine[i] === '"') { if (quoted && firstLine[i + 1] === '"') i++; else quoted = !quoted; }
    else if (!quoted && firstLine[i] in counts) counts[firstLine[i]]++;
  }
  const delimiter = extension === 'tsv' ? '\t' : Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  return parseCsv(source, { maxRows, delimiter, trimValues: false });
}

export async function readCandidateFile(file, maxRows) {
  if (file.size > 8 * 1024 * 1024) throw new Error('Choose a file smaller than 8 MB.');
  const extension = file.name.split('.').pop().toLowerCase();
  if (extension === 'xlsx') {
    const { default: readXlsxFile, readSheetNames } = await import('read-excel-file');
    const sheets = await readSheetNames(file);
    if (sheets.length > 1) throw new Error('This workbook has multiple sheets. Save the candidate sheet as a separate XLSX or CSV file so no sheets are silently omitted.');
    const cells = await readXlsxFile(file);
    return parseCandidateText(cells.map(row => row.map(value => `"${cellText(value).replace(/"/g, '""')}"`).join(',')).join('\n'), 'csv', maxRows);
  }
  if (!['csv', 'tsv', 'json', 'jsonl', 'ndjson', 'xml'].includes(extension)) throw new Error('Choose CSV, TSV, XLSX, JSON, JSONL, NDJSON, or XML. Export proprietary ATS files to one of these formats first.');
  return parseCandidateText(new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer()), extension, maxRows);
}
